import {
  CellValueChangedEvent,
  ColDef,
  GridReadyEvent,
  IServerSideGetRowsParams,
  SelectionChangedEvent,
} from '@ag-grid-community/core'
import { Input, Layout as AdLayout } from '@surecompanies/core_components'
import { message } from 'antd'
import axios from 'axios'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import { SortDirection } from 'enrollment-platform/types/sorted-request.dto'
import Page from 'layouts/Page'
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import PageWrapper from '../../../../PageWrapper'
import { CensusFileRowCountsDto } from '../../api/dto/census-file-row-counts.dto'
import { CensusFileDto } from '../../api/dto/census-file.dto'
import { CensusFileRowStatusEnum } from '../../api/enums/census-file-row-status.enum'
import { CensusFileRowTypeEnum } from '../../api/enums/census-file-row-type.enum'
import FileInfo from '../../components/FileInfo'
import GridWrapper from '../../components/GridWrapper'
import {
  CENSUS_REVIEW_PATH,
  DEFAULT_ROWS_PAGINATION_LIMIT,
} from '../../constants'
import { useCensusV2Clients } from '../../hooks/use-census-v2-clients'
import style from '../../pages/CensusManagement.module.scss'
import { CensusFileRowGridData } from '../../types/census-file-row-grid-data'
import { colDefsFromData } from '../../utils/col-defs-from-data'
import ReviewTabs from '../components/CensusReviewTabs'
import { getInReviewPageData } from '../utils/get-in-review-page-data'

const { Content } = AdLayout

interface CensusReviewRecordsBaseProps {
  pageTitle?: string
  showTabs?: boolean
  tabKey: string
  routeParams: { importPublicKey: string }
  actionButton?: ({
    censusRows,
    handleSubmit,
    selectedRows,
    counts,
  }: {
    censusRows: CensusFileRowGridData[]
    selectedRows: CensusFileRowGridData[]
    handleSubmit: () => void
    counts?: CensusFileRowCountsDto
  }) => ReactElement<any>
  rowQueryParams: {
    censusRowType?: CensusFileRowTypeEnum | CensusFileRowTypeEnum[]
    censusFileRowStatuses: CensusFileRowStatusEnum[]
    excludeMatch?: boolean
  }
  handleCellValueChanged?: (
    cellValueChangedEvent: CellValueChangedEvent
  ) => void
  // function called by ReviewRecordsBase to customize base column definitions
  modifyColDefs?: (
    baseColDefs: ColDef<CensusFileRowGridData>[]
  ) => ColDef<CensusFileRowGridData>[]
  onSelectionChangedCallback?: (selected: CensusFileRowGridData[]) => void
  onSelectAllCallback?: (total: number) => void
  onGridUtilsReady?: (utils: {
    updateGridCell: (rowId: string, columnKey: string, newValue: any) => void
    resetGrid: () => void
  }) => void
  showSearch?: boolean
  // Passed straight through to AG Grid's `context` — cell renderers read it
  // via params.context to access frequently-changing state (e.g. ignore toggles)
  // without requiring a full column-definition re-render.
  gridContext?: Record<string, any>
}

// If sorting  by a column name that has data in 'rowData', we have to apply a prefix to the field name so that the backend recognizes that the
// field is in 'rowData'
const getSortParams = (
  params: IServerSideGetRowsParams<CensusFileRowGridData>
): [string, SortDirection] => {
  const sortModel = params.request.sortModel

  const { colId, sort: direction } = sortModel[0] // Take the first sort entry
  const colDef = params.api.getColumnDef(colId)
  const prefix = colDef?.['sortPrefix'] ?? ''

  const sort = `${prefix}${colId}`
  const order =
    direction.toUpperCase() === 'DESC' ? SortDirection.desc : SortDirection.asc
  return [sort, order]
}

function CensusReviewRecordsBase({
  showTabs = true,
  routeParams,
  actionButton,
  rowQueryParams,
  handleCellValueChanged,
  modifyColDefs,
  tabKey,
  onSelectionChangedCallback,
  onSelectAllCallback,
  onGridUtilsReady,
  pageTitle = 'Review New & Updated Records',
  showSearch = false,
  gridContext,
}: CensusReviewRecordsBaseProps) {
  const {
    censusRowType: paramCensusRowType,
    censusFileRowStatuses,
    excludeMatch,
  } = rowQueryParams
  const [counts, setCounts] = useState<CensusFileRowCountsDto | undefined>()
  const [censusFile, setCensusFile] = useState<CensusFileDto | undefined>()
  const [contributionGroups, setContributionGroups] = useState<string[]>([])
  const { importPublicKey: publicKey } = routeParams
  const [censusRowType, setCensusRowType] = useState<
    CensusFileRowTypeEnum | CensusFileRowTypeEnum[] | undefined
  >(paramCensusRowType)
  const [selectedRows, setSelectedRows] = useState<CensusFileRowGridData[]>([])
  const [censusRows, setCensusRows] = useState<CensusFileRowGridData[]>([])
  const [gridReady, setGridReady] = useState<boolean>(false)
  const searchTermRef = useRef('')
  const cachedFilteredRowsRef = useRef<CensusFileRowGridData[] | null>(null)
  const lastSortModelRef = useRef<string>('')
  const gridRef = useRef<GridReadyEvent | undefined>(undefined)
  const totalRowsRef = useRef(0)
  // State to store dynamically generated column definitions
  const [dynamicColDefs, setDynamicColDefs] = useState<
    ColDef<CensusFileRowGridData>[]
  >([])
  const { clients, error } = useCensusV2Clients()
  if (error) {
    const msg = `Error loading companies: ${error.message}`
    console.error(msg)
    message.error(msg, 5)
  }

  // Get client name helper
  const clientName = (key) => clients.find((c) => c.id === key)?.name ?? '...'

  const resetGrid = useCallback(() => {
    gridRef.current?.api.refreshServerSide({ purge: true })
    gridRef.current?.api.deselectAll()
    setSelectedRows([])
  }, [])

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridRef.current = params
    setGridReady(true)
    if (onGridUtilsReady) {
      onGridUtilsReady({
        updateGridCell: (rowId, columnKey, newValue) => {
          const rowNode = params.api.getRowNode(rowId)
          if (rowNode) {
            rowNode.setDataValue(columnKey, newValue)
          }
        },
        resetGrid: resetGrid,
      })
    }
  }, [])

  useEffect(() => {
    if (gridRef.current && gridReady) {
      gridRef.current?.api.refreshServerSide({ purge: true })
    }
  }, [censusRowType, gridRef.current, gridReady])

  // Merge in contributionGroups (fetched here in Base) so cell editors can
  // read it via params.context without the parent needing to know about it.
  const combinedGridContext = useMemo(
    () => ({ ...gridContext, contributionGroups }),
    [gridContext, contributionGroups]
  )

  useEffect(() => {
    if (!gridRef.current) return
    const api = gridRef.current.api
    api.setGridOption('context', combinedGridContext)
    api.refreshCells({ force: true })
    setTimeout(() => api.resetRowHeights(), 0)
  }, [combinedGridContext])

  const onTabChange = useCallback((tab: string) => {
    setCensusRowType(tab as CensusFileRowTypeEnum)
    setSelectedRows([])
    searchTermRef.current = ''
    cachedFilteredRowsRef.current = null
    lastSortModelRef.current = ''
  }, [])

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.api.updateGridOptions({
        serverSideDatasource: { getRows },
      })
    }
  }, [gridReady])

  const buildColDefs = (
    mergedData: CensusFileRowGridData[],
    currentCensusRowType:
      | CensusFileRowTypeEnum
      | CensusFileRowTypeEnum[]
      | undefined
  ) => {
    let newColDefs = colDefsFromData(mergedData[0].rowData)
    if (currentCensusRowType === CensusFileRowTypeEnum.ERROR) {
      const errorKeys = new Set<string>()
      mergedData.forEach((row) => {
        if (row.errors) {
          Object.keys(row.errors).forEach((errorKey) => errorKeys.add(errorKey))
        }
      })
      newColDefs = [
        ...newColDefs.filter((col) => errorKeys.has(col.field ?? '')),
        ...newColDefs.filter((col) => !errorKeys.has(col.field ?? '')),
      ]
    }
    return newColDefs
  }

  // Function to fetch and set rows, also dynamically generates `colDefs`
  const getRows = async (
    params: IServerSideGetRowsParams<CensusFileRowGridData>
  ) => {
    const limit = DEFAULT_ROWS_PAGINATION_LIMIT
    let sort: string | undefined
    let order: SortDirection | undefined
    try {
      const { startRow, sortModel } = params.request

      if (sortModel.length > 0) {
        ;[sort, order] = getSortParams(params)
      }

      const currentSearch = searchTermRef.current

      if (currentSearch) {
        const currentSortKey = JSON.stringify(params.request.sortModel)
        if (
          !cachedFilteredRowsRef.current ||
          lastSortModelRef.current !== currentSortKey
        ) {
          lastSortModelRef.current = currentSortKey
          const {
            rowCounts,
            data,
            censusFile: cf,
            contributionGroups: cg,
          } = await getInReviewPageData({
            limit: totalRowsRef.current || DEFAULT_ROWS_PAGINATION_LIMIT,
            page: 1,
            publicKey,
            sort,
            order,
            censusRowType,
            censusFileRowStatuses,
            excludeMatch,
            search: currentSearch,
          })
          setCounts(rowCounts)
          setCensusFile(cf)
          setContributionGroups(cg ?? [])
          const allMerged: CensusFileRowGridData[] = data.map((d) => ({
            ...d,
            ...d.rowData,
          }))
          if (allMerged.length > 0 && dynamicColDefs.length === 0) {
            setDynamicColDefs(buildColDefs(allMerged, censusRowType))
          }
          cachedFilteredRowsRef.current = allMerged
        }

        const filtered = cachedFilteredRowsRef.current
        const pageStart = startRow ?? 0
        const slice = filtered.slice(pageStart, pageStart + limit)
        setCensusRows(slice)
        params.success({ rowData: slice, rowCount: filtered.length })
        return
      }

      // Normal server-side pagination
      cachedFilteredRowsRef.current = null
      const page = Math.floor((startRow ?? 1) / limit) + 1
      const { rowCounts, data, pagination, censusFile, contributionGroups } =
        await getInReviewPageData({
          limit,
          page,
          publicKey,
          sort,
          order,
          censusRowType,
          censusFileRowStatuses,
          excludeMatch,
        })

      setCounts(rowCounts)
      setCensusFile(censusFile)
      setContributionGroups(contributionGroups ?? [])
      const mergedData: CensusFileRowGridData[] = data.map((d) => ({
        ...d,
        ...d.rowData,
      }))
      setCensusRows(mergedData)

      if (mergedData.length > 0 && dynamicColDefs.length === 0) {
        setDynamicColDefs(buildColDefs(mergedData, censusRowType))
      }

      totalRowsRef.current = pagination.total ?? 0
      params.success({
        rowData: mergedData,
        rowCount: pagination.total,
      })
    } catch (error) {
      if (axios.isCancel(error)) return
      console.error('Error fetching data:', error)
      params.fail()
    }
  }

  const onCellValueChanged = useCallback(
    (params: CellValueChangedEvent<CensusFileRowGridData>) => {
      handleCellValueChanged && handleCellValueChanged(params)
    },
    []
  )

  const handleSearch = (value: string) => {
    const term = value.trim()
    searchTermRef.current = term
    cachedFilteredRowsRef.current = null
    lastSortModelRef.current = ''
    setSelectedRows([])
    gridRef.current?.api.deselectAll()
    gridRef.current?.api.refreshServerSide({ purge: true })
  }

  // Apply `modifyColDefs` function if provided — memoized so AG Grid doesn't
  // see a new columnDefs reference on every render and reset row heights
  const finalColDefs = useMemo(
    () => (modifyColDefs ? modifyColDefs(dynamicColDefs) : dynamicColDefs),
    [modifyColDefs, dynamicColDefs]
  )

  // keep track of rows selected in grid
  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<CensusFileRowGridData>) => {
      setTimeout(() => {
        const selectedRows: CensusFileRowGridData[] = []

        event.api.forEachNode((node) => {
          if (node.isSelected() && node.data) {
            selectedRows.push(node.data)
          }
        })

        setSelectedRows(selectedRows)
        onSelectionChangedCallback?.(selectedRows)

        if (
          (event.source === 'uiSelectAll' ||
            event.source === 'uiSelectAllCurrentPage') &&
          selectedRows.length > 0
        ) {
          onSelectAllCallback?.(totalRowsRef.current)
        }
      }, 0)
    },
    [onSelectionChangedCallback, onSelectAllCallback]
  )

  return (
    <PageWrapper>
      <Page
        title={pageTitle}
        action={
          actionButton &&
          actionButton({
            censusRows,
            selectedRows,
            handleSubmit: resetGrid,
            counts,
          })
        }
        breadcrumb={
          <BreadcrumbNav
            items={[
              { title: 'Census Review', path: CENSUS_REVIEW_PATH },
              { title: pageTitle },
            ]}
          />
        }
      >
        <AdLayout>
          <Content className={style.content}>
            <FileInfo
              data={censusFile}
              clientName={clientName(censusFile?.clientPublicKey)}
              style={{ padding: '15px 0 0 15px' }}
            />
            <ReviewTabs
              showTabs={showTabs}
              publicKey={publicKey}
              counts={counts}
              onTabChange={onTabChange}
              currentTab={tabKey}
              style={{ padding: '15px 0 0 15px' }}
            />
            {/* Search field */}
            {showSearch && (
              <div style={{ padding: '8px 15px' }}>
                <Input.Search
                  placeholder="Search by first or last name"
                  allowClear
                  onSearch={handleSearch}
                  style={{ width: 280 }}
                />
              </div>
            )}
            <GridWrapper
              onSelectionChanged={onSelectionChanged}
              columnDefs={finalColDefs}
              onGridReady={onGridReady}
              rowSelection="multiple"
              onCellValueChanged={onCellValueChanged}
              additionalProps={{ context: combinedGridContext }}
            />
          </Content>
        </AdLayout>
      </Page>
    </PageWrapper>
  )
}

export default CensusReviewRecordsBase
